import React, { useEffect, useState } from 'react';
import { ArrowLeft, RefreshCw, ThumbsUp, ThumbsDown, Trash2, ChevronUp, ChevronDown, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getFeedbackRecommendations, removeFeedback } from '../lib/recommendationService';
import { useAuth } from '../contexts/AuthContext';
import { Recommendation as SharedRecommendation } from '../types';
import { Tab } from '@headlessui/react';
import SEO from '../components/SEO';

// Função auxiliar para formatar data
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export default function FeedbackPage() {
  const { user } = useAuth();
  const [likedGames, setLikedGames] = useState<SharedRecommendation[]>([]);
  const [dislikedGames, setDislikedGames] = useState<SharedRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingFeedback, setRemovingFeedback] = useState<Record<string, boolean>>({});
  const [expandedGame, setExpandedGame] = useState<Record<string, boolean>>({});
  const [success, setSuccess] = useState<string | null>(null);

  // Carregar jogos avaliados pelo usuário
  const loadFeedbackGames = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user) {
        setError('Você precisa estar logado para ver seus feedbacks');
        setLoading(false);
        return;
      }

      const liked = await getFeedbackRecommendations(user.id, true);
      const disliked = await getFeedbackRecommendations(user.id, false);

      // Ordenar por data de feedback (mais recente primeiro)
      const sortByDate = (a: SharedRecommendation, b: SharedRecommendation) => {
        // Garantir que created_at seja uma string válida
        const dateA = a.created_at || new Date().toISOString();
        const dateB = b.created_at || new Date().toISOString();
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      };

      setLikedGames(liked.sort(sortByDate));
      setDislikedGames(disliked.sort(sortByDate));
    } catch (error) {
      console.error('Erro ao carregar jogos avaliados:', error);
      setError('Não foi possível carregar suas avaliações. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeedbackGames();
  }, [user]);

  // Remover feedback do jogo
  const handleRemoveFeedback = async (gameId: string, isLiked: boolean) => {
    try {
      setRemovingFeedback(prev => ({ ...prev, [gameId]: true }));
      await removeFeedback(gameId);
      
      // Atualizar a lista correspondente
      if (isLiked) {
        setLikedGames(prev => prev.filter(game => game.id !== gameId));
      } else {
        setDislikedGames(prev => prev.filter(game => game.id !== gameId));
      }
      
      setSuccess('Feedback removido com sucesso');
      
      // Limpar mensagem de sucesso após 3 segundos
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error) {
      console.error('Erro ao remover feedback:', error);
      setError('Não foi possível remover o feedback. Tente novamente.');
      
      // Limpar mensagem de erro após 3 segundos
      setTimeout(() => {
        setError(null);
      }, 3000);
    } finally {
      setRemovingFeedback(prev => ({ ...prev, [gameId]: false }));
    }
  };

  // Alternar expansão do card do jogo
  const toggleGameExpand = (gameId: string) => {
    setExpandedGame(prev => ({
      ...prev,
      [gameId]: !prev[gameId]
    }));
  };

  // Função segura para renderizar texto
  const safeText = (text: unknown): string => {
    if (typeof text === 'string') return text;
    return '';
  };

  // Renderizar card de feedback individual
  const renderFeedbackItem = (game: SharedRecommendation, isLiked: boolean) => {
    const isExpanded = expandedGame[game.id] || false;
    const image = game.game?.image_url || 'https://placehold.co/600x400?text=Sem+Imagem';
    
    return (
      <div key={game.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-4 dark:bg-gray-800 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row">
          {/* Imagem do jogo */}
          <div className="sm:w-1/4 md:w-1/5 h-32 sm:h-auto overflow-hidden relative">
            <Link to={game.game?.slug ? `/games/${game.game.slug}` : `/games/${game.game?.id}`}>
              <img 
                src={image} 
                alt={game.game?.name || 'Jogo'} 
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://placehold.co/600x400?text=Erro+ao+carregar';
                }}
              />
            </Link>
          </div>
          
          {/* Informações do jogo */}
          <div className="p-4 sm:w-3/4 md:w-4/5 flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-bold text-gray-800 text-lg dark:text-gray-100">
                  <Link 
                    to={game.game?.slug ? `/games${game.game.slug}` : `/games/${game.game?.id}`}
                    className="hover:text-indigo-600 dark:hover:text-indigo-400"
                  >
                    {game.game?.name || 'Jogo desconhecido'}
                  </Link>
                </h3>
                <button 
                  onClick={() => toggleGameExpand(game.id)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
              </div>
              
              <div className="flex items-center mb-2 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center mr-4">
                  {isLiked ? 
                    <ThumbsUp size={16} className="text-green-500 mr-1 dark:text-green-400" /> : 
                    <ThumbsDown size={16} className="text-red-500 mr-1 dark:text-red-400" />
                  }
                  <span>{isLiked ? 'Gostei' : 'Não gostei'}</span>
                </div>
                <div>
                  {game.created_at ? formatDate(game.created_at) : 'Data desconhecida'}
                </div>
              </div>
              
              {isExpanded && (
                <div className="mt-3 space-y-2 text-sm">
                  {game.reason && (
                    <div className="mb-3">
                      <p className="font-medium text-gray-700 mb-1 dark:text-gray-300">Motivo da recomendação:</p>
                      <p className="text-gray-600 italic dark:text-gray-400">{safeText(game.reason)}</p>
                    </div>
                  )}
                  {game.game?.year && (
                    <p className="dark:text-gray-300"><span className="font-medium">Ano:</span> {game.game.year}</p>
                  )}
                  {game.game?.min_players && game.game?.max_players && (
                    <p className="dark:text-gray-300"><span className="font-medium">Jogadores:</span> {game.game.min_players} - {game.game.max_players}</p>
                  )}
                  {game.game?.description && (
                    <p className="mt-2 text-gray-700 dark:text-gray-300">{game.game.description.substring(0, 150)}...</p>
                  )}
                </div>
              )}
            </div>
            
            <div className="mt-3 flex space-x-2">
              <Link
                to={game.game?.slug ? `/games/${game.game.slug}` : `/games/${game.game?.id}`}
                className="inline-flex items-center px-3 py-1.5 text-xs text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
              >
                <ExternalLink size={14} className="mr-1" />
                Ver detalhes
              </Link>
              <button
                onClick={() => handleRemoveFeedback(game.id, isLiked)}
                disabled={removingFeedback[game.id]}
                className="inline-flex items-center px-3 py-1.5 text-xs text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
              >
                <Trash2 size={14} className="mr-1" />
                {removingFeedback[game.id] ? 'Removendo...' : 'Remover'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Renderizar conteúdo da página
  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-8 md:py-12">
          <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-t-2 border-b-2 border-indigo-500 mx-auto dark:border-indigo-400"></div>
          <p className="mt-4 text-gray-600 text-sm md:text-base dark:text-gray-400">Carregando seus feedbacks...</p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden dark:bg-gray-800 dark:border dark:border-gray-700">
        <Tab.Group>
          <Tab.List className="flex border-b border-gray-200 dark:border-gray-700">
            <Tab className={({ selected }) => 
              `flex-1 py-3 px-2 text-sm font-medium focus:outline-none relative
              ${selected 
                ? 'text-indigo-600 border-b-2 border-indigo-600 dark:text-indigo-400 dark:border-indigo-500' 
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`
            }>
              <div className="flex items-center justify-center">
                <ThumbsUp className="w-4 h-4 mr-2" />
                <span>Gostei</span>
                {likedGames.length > 0 && (
                  <span className="ml-2 bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full text-xs dark:bg-indigo-900/30 dark:text-indigo-300">
                    {likedGames.length}
                  </span>
                )}
              </div>
            </Tab>
            <Tab className={({ selected }) => 
              `flex-1 py-3 px-2 text-sm font-medium focus:outline-none
              ${selected 
                ? 'text-red-600 border-b-2 border-red-600 dark:text-red-400 dark:border-red-500' 
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`
            }>
              <div className="flex items-center justify-center">
                <ThumbsDown className="w-4 h-4 mr-2" />
                <span>Não gostei</span>
                {dislikedGames.length > 0 && (
                  <span className="ml-2 bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs dark:bg-red-900/30 dark:text-red-300">
                    {dislikedGames.length}
                  </span>
                )}
              </div>
            </Tab>
          </Tab.List>
          <Tab.Panels>
            <Tab.Panel>
              {likedGames.length === 0 ? (
                <div className="text-center py-6 text-gray-500 italic text-sm dark:text-gray-400">
                  Nenhum jogo curtido ainda
                </div>
              ) : (
                <div className="p-4">
                  {likedGames.map(game => renderFeedbackItem(game, true))}
                </div>
              )}
            </Tab.Panel>
            <Tab.Panel>
              {dislikedGames.length === 0 ? (
                <div className="text-center py-6 text-gray-500 italic text-sm dark:text-gray-400">
                  Nenhum jogo descurtido ainda
                </div>
              ) : (
                <div className="p-4">
                  {dislikedGames.map(game => renderFeedbackItem(game, false))}
                </div>
              )}
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    );
  };

  return (
    <>
      <SEO 
        title="Meus Feedbacks"
        description="Veja os jogos que você gostou e não gostou no Boardverso."
      />
      <div className="container mx-auto max-w-6xl px-4 py-8">
        <div className="flex items-center mb-6">
          <Link to="/" className="flex items-center text-indigo-600 hover:text-indigo-800 mr-4 dark:text-indigo-400 dark:hover:text-indigo-300">
            <ArrowLeft size={20} className="mr-1" />
            <span className="text-sm">Voltar</span>
          </Link>
          <h1 className="text-2xl font-bold text-gray-800 flex-grow dark:text-gray-100">Meus Feedbacks</h1>
          <button
            onClick={loadFeedbackGames}
            className="flex items-center text-gray-600 hover:text-gray-800 text-sm dark:text-gray-300 dark:hover:text-gray-100"
            disabled={loading}
          >
            <RefreshCw size={16} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
            <span>Atualizar</span>
          </button>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6 text-sm md:text-base dark:bg-red-900/30 dark:text-red-300">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-6 text-sm md:text-base dark:bg-green-900/30 dark:text-green-300">
            {success}
          </div>
        )}

        {renderContent()}
      </div>
    </>
  );
} 